import { useEffect, useState, type ReactNode } from "react";
import { StatusField } from "@steelyard/theme";
import { getBrand, getCategory, getSku, listBrands, listCategoryTree, listSkus } from "../lib/services/catalog";

type Option = { value: string; label: string };

type PickerProps = {
  value: string;
  onChange: (id: string) => void;
  error?: string;
  disabled?: boolean;
  /** For an optional filter: an × that empties the choice. */
  clearable?: boolean;
  /** Shows a visible `*` next to the label, same treatment as `Field`/`SelectField`'s `required`. */
  required?: boolean;
  /** Why this picker is disabled — shown in place of a hint while `disabled` is true, same shape as `SelectField`'s `disabledReason`. */
  disabledReason?: ReactNode;
};

/** `label` + a `text-red` asterisk when `required` — the same visible treatment `Field`/`SelectField` give a required field. */
function pickerLabel(label: string, required?: boolean): ReactNode {
  if (!required) return label;
  return (
    <>
      {label}
      <span aria-hidden="true" className="text-red">
        {" "}
        *
      </span>
    </>
  );
}

/** Every active SKU, a page of the API's 200-row cap at a time, until the last page. */
async function listAllActiveSkus(): Promise<Option[]> {
  const options: Option[] = [];
  for (let page = 1; ; page++) {
    const result = await listSkus({ page, pageSize: 200, activeOnly: true });
    options.push(...result.items.map((sku) => ({ value: sku.id, label: `${sku.code} — ${sku.name}` })));
    if (result.items.length < 200 || page >= Number(result.totalPages ?? page)) return options;
  }
}

/**
 * ORD-03/PAK-12: the active SKUs as a plain list — open it and choose, no typing first (the owner
 * asked for the list, not a search box). Fetched once, same shape as BrandPicker.
 * ponytail: fine at today's few dozen SKUs; at several thousand, bring back type-to-filter.
 */
export function SkuPicker({ value, onChange, error, disabled, clearable, required, disabledReason }: PickerProps) {
  const [options, setOptions] = useState<Option[]>([]);
  // Same "loading vs. genuinely empty" distinction as BrandPicker.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listAllActiveSkus()
      .then((listed) => {
        if (cancelled) return;
        // Merged, not replaced: see BrandPicker's identical note.
        setOptions((prev) => [...listed, ...prev.filter((p) => !listed.some((l) => l.value === p.value))]);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Seeds the label for a SKU the active list doesn't carry — editing a row whose SKU has since
  // been deactivated.
  useEffect(() => {
    if (!value || options.some((option) => option.value === value)) return;
    let cancelled = false;
    getSku(value)
      .then((sku) => {
        if (!cancelled) setOptions((prev) => [{ value: sku.id, label: `${sku.code} — ${sku.name}` }, ...prev]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-seeds for a genuinely new value, not every options change
  }, [value]);

  const empty = loaded && options.length === 0;
  return (
    <StatusField
      label={pickerLabel("SKU", required)}
      // The full active list shows on open; typing filters it locally (code or name).
      searchable
      searchPlaceHolder="Type a SKU code or name…"
      // StatusField pins options at h-9; a long SKU name wraps, so let the row grow instead of
      // overlapping the next one.
      optionClassName="h-auto min-h-9 py-2 leading-snug"
      options={options}
      value={options.find((option) => option.value === value) ?? null}
      onChange={(pickedValue: string | null) => onChange(pickedValue ?? "")}
      getOptionValue={(option) => option.value}
      placeholder={!loaded ? "Loading SKUs…" : empty ? "No active SKUs" : "Choose a SKU…"}
      error={error}
      disabled={disabled || empty}
      helperText={disabled && disabledReason ? disabledReason : empty ? "No active SKUs." : undefined}
      helperClassName="text-muted-foreground text-xs"
      clearable={clearable && Boolean(value)}
      onClear={() => onChange("")}
      // StatusField forces inPortal={false} (fine for a lone toolbar filter with nothing below
      // it); this picker sits inside repeatable form rows with fields after it, so its locally
      // absolute-positioned, z-0-forced dropdown was rendering underneath those later-in-DOM-order
      // siblings — invisible and unclickable. Overriding back to a portal renders it in one shared
      // floating layer instead, above the page and immune to any row's own stacking context.
      inPortal
    />
  );
}

/** A small, bounded list (roughly 15 brands) — fetched once and filtered locally, same as the portal's own brand filter. */
export function BrandPicker({ value, onChange, error, disabled, required, disabledReason }: PickerProps) {
  const [options, setOptions] = useState<Option[]>([]);
  // Distinguishes "still loading" from "genuinely no brands" — the closed control must not claim
  // there are none until the one-shot fetch below has actually come back.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listBrands(1, true, 200).then((result) => {
      if (cancelled) return;
      // Merged, not replaced: this can resolve after the seed effect below adds a brand the
      // active-only list doesn't carry, and replacing would silently drop it again.
      const listed = result.items.map((brand) => ({ value: brand.id, label: brand.name }));
      setOptions((prev) => [...listed, ...prev.filter((p) => !listed.some((l) => l.value === p.value))]);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Seeds the label for an inactive brand a standing rule still points at — active-only above
  // would otherwise silently drop it from the list.
  useEffect(() => {
    if (!value || options.some((option) => option.value === value)) return;
    let cancelled = false;
    getBrand(value)
      .then((brand) => {
        if (!cancelled) setOptions((prev) => [{ value: brand.id, label: brand.name }, ...prev]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const empty = loaded && options.length === 0;
  return (
    <StatusField
      label={pickerLabel("Brand", required)}
      searchable
      options={options}
      value={options.find((option) => option.value === value) ?? null}
      onChange={(pickedValue: string | null) => onChange(pickedValue ?? "")}
      getOptionValue={(option) => option.value}
      placeholder={empty ? "No brands available" : "Choose a brand…"}
      error={error}
      disabled={disabled || empty}
      helperText={disabled && disabledReason ? disabledReason : empty ? "No brands available." : undefined}
      helperClassName="text-muted-foreground text-xs"
      inPortal
    />
  );
}

/** The whole category tree, fetched once — the same bounded structure `categories/List.tsx` already renders whole. */
export function CategoryPicker({ value, onChange, error, disabled, required, disabledReason }: PickerProps) {
  const [options, setOptions] = useState<Option[]>([]);
  // Same "loading vs. genuinely empty" distinction as BrandPicker.
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    listCategoryTree(true).then((result) => {
      if (cancelled) return;
      // Merged, not replaced: see BrandPicker's identical note.
      const listed = result.items.map((category) => ({ value: category.id, label: `${"— ".repeat(category.depth)}${category.name}` }));
      setOptions((prev) => [...listed, ...prev.filter((p) => !listed.some((l) => l.value === p.value))]);
      setLoaded(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!value || options.some((option) => option.value === value)) return;
    let cancelled = false;
    getCategory(value)
      .then((category) => {
        if (!cancelled) setOptions((prev) => [{ value: category.id, label: category.name }, ...prev]);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const empty = loaded && options.length === 0;
  return (
    <StatusField
      label={pickerLabel("Category", required)}
      searchable
      options={options}
      value={options.find((option) => option.value === value) ?? null}
      onChange={(pickedValue: string | null) => onChange(pickedValue ?? "")}
      getOptionValue={(option) => option.value}
      placeholder={empty ? "No categories available" : "Choose a category…"}
      error={error}
      disabled={disabled || empty}
      helperText={disabled && disabledReason ? disabledReason : empty ? "No categories available." : undefined}
      helperClassName="text-muted-foreground text-xs"
      inPortal
    />
  );
}
